import {Component, Inject, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {setTimerValues, TIMER_ARRAY_UNITS} from '../../helpers/time-array-helper';
import {setSelectedLanguage} from "../../../state/study/study.actions";
import {Language} from "../../../../models/language";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../state";
import {combineLatest, Observable, Subscription} from "rxjs";
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages
} from "../../../state/study/study.selectors";
import {Study} from "../../../../models/study";
import {PrivilegeService} from "../../../services/privilege/privilege.service";
import {API_DATE_FORMAT, PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS} from "../../../../core/dashboard-constants";
import {Module} from "../../../../models/module";
import {selectSelectedModule} from "../../../state/module/module.selectors";
import {StudyType} from 'src/app/models/study-type';
import {Notification} from "../../../../models/notification";
import {NotificationCardView} from "../../../../models/notificationCardView";
import {NotificationService} from "../../../services/notification/notification.service";
import {ToastrService} from "ngx-toastr";
import {getLanguageByCode} from "../../../../core/languages-helper";
import * as moment from "moment";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {InterventionContentItem} from "../../../../models/intervention-content-item";
import {Trigger} from "../../../../models/trigger";

const FIXED_DATE = 'fixed_date';
const BASED_ON_ANSWER = 'based_on_answer';

@Component({
  selector: 'dashboard-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss']
})
export class NotificationComponent implements OnInit {

  TIMER_ARRAY_UNITS = TIMER_ARRAY_UNITS;
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  OPENING_NOTIFICATION = 'opening';
  CLOSING_NOTIFICATION = 'closing';
  REMINDER_NOTIFICATION = 'reminder';

  INTERVAL_TIMING_TYPE = 'interval';
  DATE_TIMING_TYPE = 'date';

  StudyType = StudyType;

  selectedStudy$: Observable<Study>;
  selectedStudy: Study;
  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;
  selectedModule$: Observable<Module>;
  selectedModule: Module;

  notifications: Notification[];
  reminderNotifications: Notification[];
  tempNotifications: Notification[];
  openNotification: Notification;
  closeNotification: Notification;

  private subscriptions$ = new Subscription();
  openNotificationCardView: NotificationCardView;
  closeNotificationCardView: NotificationCardView;

  reminderNotificationCardViews: NotificationCardView[];
  viewOnly: boolean;
  wasAdded = false;
  empty = true;

  now = moment(new Date());
  moduleClosingDate;
  canEditClosingNotification = false;

  constructor(@Inject(MAT_DIALOG_DATA)
              public data: { contentItem: InterventionContentItem },
              private formBuilder: FormBuilder,
              private store: Store<AppState>,
              public privilegeService: PrivilegeService,
              private notificationService: NotificationService,
              private toastService: ToastrService) {
  }

  ngOnInit(): void {
    const contentItem = this.data?.contentItem;
    this.notifications = [];
    this.tempNotifications = [];
    this.reminderNotifications = [];
    this.reminderNotificationCardViews = [];

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);

    this.subscriptions$.add(
      combineLatest([this.supportedLanguages$, this.selectedStudy$]).subscribe((data) => {
        this.supportedLanguages = data[0];
        this.selectedStudy = data[1];
        this.checkPrivileges();
        this.addNotifications();
      })
    );

    if (contentItem.triggers) {
      this.initModuleClosingDate(contentItem.triggers);
    }

    this.selectedModule$ = this.store.select(selectSelectedModule);
    this.subscriptions$.add(
      this.selectedModule$.subscribe((module) => {
        this.selectedModule = module;
        this.addNotifications();
      })
    );

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions$.add(this.selectedLanguage$.subscribe((language) => {
      this.selectedLanguage = language
    }));
  }

  initModuleClosingDate(triggers: Trigger[]) {
    triggers.forEach((trigger) => {
      trigger.rules.forEach((rule) => {
        if (rule.disable === 1 && rule.triggerType !== BASED_ON_ANSWER) {
          if (rule.triggerType === FIXED_DATE) {
            this.moduleClosingDate = rule.fixedDate;
          }
          this.canEditClosingNotification = true;
        }
      });
    });
  }

  addNotifications() {
    if (this.supportedLanguages && this.selectedModule && !this.wasAdded) {
      if (this.selectedModule.removeAfter) {
        this.canEditClosingNotification = true;
      }
      this.notificationService.getNotifications(this.selectedStudy.id, this.selectedModule.id).subscribe(notifications => {
        this.addOpenNotification(null);
        if (this.canEditClosingNotification) {
          this.addCloseNotification(null);
        }
        if (notifications.length > 0) {
          notifications.forEach(notification => {
            switch (notification.type) {
              case this.OPENING_NOTIFICATION:
                this.addOpenNotification(notification)
                break;
              case this.CLOSING_NOTIFICATION:
                if (this.canEditClosingNotification) {
                  this.addCloseNotification(notification)
                }
                break;
              case this.REMINDER_NOTIFICATION:
                this.addReminderNotification(notification)
                break;
            }
          });
        }
        this.wasAdded = true;
      }, error => {
        this.addOpenNotification(null);
        if (this.canEditClosingNotification) {
          this.addCloseNotification(null);
        }
      });
    }
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.code === event.value);
    this.store.dispatch(setSelectedLanguage({selectedLanguage: foundSelected}));
  }

  checkPrivileges(): void {
    if (this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.VIEW) &&
      !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL)) {
      this.viewOnly = true;
    } else if (this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL)) {
      this.viewOnly = false;
    }
  }

  expandNotification(notificationType: string, reminder?: Notification) {
    switch (notificationType) {
      case this.OPENING_NOTIFICATION:
        this.openNotificationCardView.expand = !this.openNotificationCardView.expand;
        break;
      case this.CLOSING_NOTIFICATION:
        this.closeNotificationCardView.expand = !this.closeNotificationCardView.expand;
        break;
      case this.REMINDER_NOTIFICATION:
        this.reminderNotificationCardViews.forEach(expandReminder => {
          if (expandReminder.notification === reminder) {
            expandReminder.expand = !expandReminder.expand;
          }
        })
        break;
    }
  }

  addOpenNotification(openNotification: Notification) {
    if (!openNotification) {
      const openNotificationTemp: Notification = {
        id: null,
        module_id: this.selectedModule.id,
        type: this.OPENING_NOTIFICATION,
        timing_type: '',
        text: [],
        timing_interval: 0,
        date: null,
        created_at: null,
        updated_at: null,
        deleted_at: null,
        weeks: null,
        days: null,
        hours: null,
        minutes: null
      }
      this.supportedLanguages.forEach(lang => {
        openNotificationTemp.text.push({languageCode: lang.code, text: ''});
      });
      this.openNotification = openNotificationTemp;
      this.notifications.push(openNotificationTemp);
      this.tempNotifications.push(JSON.parse(JSON.stringify(openNotificationTemp)));
    } else {
      this.openNotification = openNotification;
      this.notifications.push(openNotification);
      this.tempNotifications.push(JSON.parse(JSON.stringify(openNotification)));
    }

    this.openNotificationCardView = {
      expand: true,
      notification: openNotification,
      errors: []
    }
  }

  addCloseNotification(closeNotification: Notification) {
    if (!closeNotification) {
      const closeNotificationTemp: Notification = {
        id: null,
        module_id: this.selectedModule.id,
        type: this.CLOSING_NOTIFICATION,
        timing_type: this.INTERVAL_TIMING_TYPE,
        text: [],
        timing_interval: 0,
        date: null,
        created_at: null,
        updated_at: null,
        deleted_at: null,
        weeks: null,
        days: null,
        hours: null,
        minutes: null
      }
      this.supportedLanguages.forEach(lang => {
        closeNotificationTemp.text.push({languageCode: lang.code, text: ''});
      });
      this.closeNotification = closeNotificationTemp;
      this.notifications.push(closeNotificationTemp);
      this.tempNotifications.push(JSON.parse(JSON.stringify(closeNotificationTemp)));
    } else {
      if (closeNotification.timing_interval !== 0) {
        this.fillTimingInterval(closeNotification);
      }
      this.closeNotification = closeNotification;
      this.notifications.push(closeNotification);
      this.tempNotifications.push(JSON.parse(JSON.stringify(closeNotification)));
    }

    this.closeNotificationCardView = {
      expand: true,
      notification: closeNotification,
      errors: []
    }
  }

  addReminderNotification(reminderNotification: Notification) {
    if (!reminderNotification) {
      reminderNotification = {
        id: null,
        module_id: this.selectedModule.id,
        type: this.REMINDER_NOTIFICATION,
        timing_type: this.INTERVAL_TIMING_TYPE,
        text: [],
        timing_interval: 0,
        date: null,
        created_at: null,
        updated_at: null,
        deleted_at: null,
        weeks: null,
        days: null,
        hours: null,
        minutes: null
      }
      this.supportedLanguages.forEach(lang => {
        reminderNotification.text.push({languageCode: lang.code, text: ''});
      });
    }

    if (reminderNotification.timing_interval !== 0) {
      this.fillTimingInterval(reminderNotification);
    }

    this.reminderNotificationCardViews.push({
      expand: true,
      notification: reminderNotification,
      errors: []
    });
    this.reminderNotifications.push(reminderNotification);
    this.notifications.push(reminderNotification);
    this.tempNotifications.push(JSON.parse(JSON.stringify(reminderNotification)));
  }

  removeReminderNotification(reminderNotification: Notification) {
    if (reminderNotification.id) {
      this.notificationService.deleteNotificationById(this.selectedStudy.id, this.selectedModule.id, reminderNotification.id).subscribe(result => {
        this.removeNotification(reminderNotification);
      });
    } else {
      this.removeNotification(reminderNotification);
    }
  }

  removeNotification(notification: Notification) {
    const reminderIndex = this.reminderNotifications.indexOf(notification, 0);
    const notificationIndex = this.notifications.indexOf(notification, 0);
    const tempNotificationIndex = this.tempNotifications.indexOf(notification, 0);

    if (reminderIndex > -1) {
      this.reminderNotifications.splice(reminderIndex, 1);
    }
    if (notificationIndex > -1) {
      this.notifications.splice(notificationIndex, 1);
    }
    if (notificationIndex > -1) {
      this.tempNotifications.splice(tempNotificationIndex, 1);
    }
    this.showSuccessWithTimeout(notification.type + ' notifications was removed', 'Success', 4000);
  }

  removeCloseNotification(closeNotification: Notification) {
    if (closeNotification.id) {
      this.notificationService.deleteNotificationById(this.selectedStudy.id, this.selectedModule.id, closeNotification.id).subscribe(result => {
        const notificationIndex = this.notifications.indexOf(closeNotification, 0);
        const tempNotificationIndex = this.tempNotifications.indexOf(closeNotification, 0);

        if (notificationIndex > -1) {
          this.notifications.splice(notificationIndex, 1);
        }
        if (notificationIndex > -1) {
          this.tempNotifications.splice(tempNotificationIndex, 1);
        }

        this.addCloseNotification(null);

        this.showSuccessWithTimeout(closeNotification.type + ' notifications was removed', 'Success', 4000);
      });
    }
  }

  removeOpenNotification(openNotification: Notification) {
    if (openNotification.id) {
      this.notificationService.deleteNotificationById(this.selectedStudy.id, this.selectedModule.id, openNotification.id).subscribe(result => {
        const notificationIndex = this.notifications.indexOf(openNotification, 0);
        const tempNotificationIndex = this.tempNotifications.indexOf(openNotification, 0);

        if (notificationIndex > -1) {
          this.notifications.splice(notificationIndex, 1);
        }
        if (notificationIndex > -1) {
          this.tempNotifications.splice(tempNotificationIndex, 1);
        }

        this.addOpenNotification(null);

        this.showSuccessWithTimeout(openNotification.type + ' notifications was removed', 'Success', 4000);
      });
    }
  }

  validateNotification(notification: Notification) {
    switch (notification.type) {
      case this.OPENING_NOTIFICATION:
        this.validateOpenNotification(notification);
        break;
      case this.CLOSING_NOTIFICATION:
        this.validateCloseAndReminderNotification(notification);
        break;
      case this.REMINDER_NOTIFICATION:
        this.validateCloseAndReminderNotification(notification);
        break;
    }
  }

  validateOpenNotification(notification: Notification) {
    if (notification.text.length > 1) {
      let amountOfEmptyText = 0;
      const languageCode = [];
      notification.text.forEach(notificationText => {
        if (notificationText.text === '') {
          amountOfEmptyText++;
          languageCode.push(notificationText.languageCode);
        }
      });
      if (amountOfEmptyText !== 0 && amountOfEmptyText !== notification.text.length) {
        for (let i = 0; i < amountOfEmptyText; i++) {
          this.showWarningWithTimeout('Please fill all the language values: ' + getLanguageByCode(languageCode[i]).name + ' is missing text in the Open notification',
            'Warning', 7000);
          this.addToError(notification, 'Missing text for ' + getLanguageByCode(languageCode[i]).name + ' in Open notification');
        }
        return false;
      } else {
        return true;
      }
    }
  }

  validateCloseAndReminderNotification(notification: Notification) {
    if (notification.text.length > 0) {
      let amountOfEmptyText = 0;
      const languageCode = [];
      notification.text.forEach(notificationText => {
        if (notificationText.text === '') {
          amountOfEmptyText++;
          languageCode.push(notificationText.languageCode);
        }
      });

      if (amountOfEmptyText !== 0 && amountOfEmptyText !== notification.text.length) {
        for (let i = 0; i < amountOfEmptyText; i++) {
          this.showWarningWithTimeout('Please fill all the language values: ' +
            getLanguageByCode(languageCode[i]).name + ' is missing text in the ' + notification.type + ' notification',
            'Warning', 7000);
          this.addToError(notification, 'Missing text for ' + getLanguageByCode(languageCode[i]).name + ' in ' + notification.type + ' notification');
        }
      }

      if (notification.timing_type === this.INTERVAL_TIMING_TYPE) {
        this.saveTimingInterval(notification);
        if (notification.timing_interval === 0 && amountOfEmptyText !== notification.text.length) {
          this.showWarningWithTimeout('There is no time indicated in the ' + notification.type +
            ' notification. Please fill in a time', 'Warning', 7000);
          this.addToError(notification, 'Missing time for ' + notification.type + ' notification');
        } else if (notification.timing_interval !== 0 && amountOfEmptyText === notification.text.length) {
          this.showWarningWithTimeout('Please fill in all the languages for the ' + notification.type + ' notification',
            'Warning', 7000);
          this.addToError(notification, 'Missing text for ' + notification.type + ' notification');
        }
      } else if (notification.timing_type === this.DATE_TIMING_TYPE) {
        if (notification.date === null && amountOfEmptyText !== notification.text.length) {
          this.showWarningWithTimeout('There is no date specified in the ' + notification.type +
            ' notification. Please pick a date', 'Warning', 7000);
          this.addToError(notification, 'Missing date for ' + notification.type + ' notification');
        } else if (notification.date !== null && amountOfEmptyText === notification.text.length) {
          this.showWarningWithTimeout('Please fill in all the languages for the ' + notification.type + ' notification',
            'Warning', 7000);
          this.addToError(notification, 'Missing text for ' + notification.type + ' notification');
        }
      }
    }
  }

  addToError(notification: Notification, errorMessage: string) {
    switch (notification.type) {
      case this.OPENING_NOTIFICATION:
        this.openNotificationCardView.errors.push(errorMessage);
        break;
      case this.CLOSING_NOTIFICATION:
        this.closeNotificationCardView.errors.push(errorMessage);
        break;
      case this.REMINDER_NOTIFICATION:
        this.setReminderErrors(notification, errorMessage);
        break;
    }
  }

  clearNotificationErrors() {
    if (this.openNotificationCardView) {
      this.openNotificationCardView.errors = [];
    }
    if (this.closeNotificationCardView) {
      this.closeNotificationCardView.errors = [];
    }
    if (this.reminderNotificationCardViews) {
      this.reminderNotificationCardViews.forEach(reminder => {
        reminder.errors = [];
      });
    }
  }

  checkIfClosingAndReminderIsEmpty(notification: Notification): boolean {
    let notificationIsEmpty = true;
    notification.text.forEach(notificationText => {
      if (notificationText.text !== '') {
        notificationIsEmpty = false;
      }
    });

    if ((notification.weeks !== 0) &&
      (notification.days !== 0) &&
      (notification.hours !== 0) &&
      (notification.minutes !== 0)) {
      notificationIsEmpty = false;
    }

    return notificationIsEmpty;
  }

  getExpandReminder(reminder: Notification): boolean {
    let expand: boolean;
    this.reminderNotificationCardViews.forEach(expandReminder => {
      if (expandReminder.notification === reminder) {
        expand = expandReminder.expand;
      }
    });
    return expand;
  }

  getReminderErrors(reminder: Notification): string[] {
    let errors: string[];
    this.reminderNotificationCardViews.forEach(reminderCardView => {
      if (reminderCardView.notification === reminder) {
        errors = reminderCardView.errors;
      }
    });
    return errors;
  }

  setReminderErrors(reminder: Notification, errorMessage: string) {
    this.reminderNotificationCardViews.forEach(reminderCardView => {
      if (reminderCardView.notification === reminder) {
        reminderCardView.errors.push(errorMessage);
      }
    });
  }

  saveNotifications() {
    this.clearNotificationErrors();
    this.notifications.forEach(notification => {
      this.validateNotification(notification);
    });

    if (!this.notificationsHasErrors()) {
      this.notifications.forEach(notification => {
        if (notification.type === this.CLOSING_NOTIFICATION || notification.type === this.REMINDER_NOTIFICATION) {
          if (!this.checkIfClosingAndReminderIsEmpty(notification)) {
            this.saveTimingInterval(notification);
            this.createOrUpdate(notification);
          }
        } else {
          this.createOrUpdate(notification);
        }
      });
    }
  }

  createOrUpdate(notification: Notification) {
    if (!this.sameData(notification, this.getTempNotification(notification))) {
      if (notification.id) {
        this.notificationService.updateNotification(this.selectedStudy.id, this.selectedModule.id, notification.id, notification).subscribe(result => {
          this.showSuccessWithTimeout(result.type + ' notifications was updated', 'Success', 4000);
        });
      } else {
        this.notificationService.createNotification(this.selectedStudy.id, this.selectedModule.id, notification).subscribe(() => {
          this.showSuccessWithTimeout(notification.type + ' notifications was created', 'Success', 4000);
        });
      }
    }
  }

  saveTimingInterval(notification: Notification) {
    notification.timing_interval = notification.weeks * 7 * 24 * 60 + notification.days * 24 * 60 + notification.hours * 60 + notification.minutes;
  }

  fillTimingInterval(notification: Notification) {
    notification.weeks = notification.timing_interval ? Math.floor(notification.timing_interval / (7 * 24 * 60)) : 0;
    notification.days = notification.timing_interval ? Math.floor((notification.timing_interval % (7 * 24 * 60)) / (24 * 60)) : 0;
    notification.hours = notification.timing_interval ? Math.floor((notification.timing_interval % (24 * 60)) / 60) : 0;
    notification.minutes = notification.timing_interval ? Math.floor(notification.timing_interval % 60) : 0;
  }

  showSuccessWithTimeout(message, title, timespan) {
    this.toastService.success(message, title, {
      timeOut: timespan
    });
  }

  showWarningWithTimeout(message, title, timespan) {
    this.toastService.warning(message, title, {
      timeOut: timespan
    });
  }

  getTempNotification(notification: Notification): Notification {
    const notificationIndex = this.notifications.indexOf(notification, 0);
    return this.tempNotifications[notificationIndex];
  }

  sameData(notificationA: Notification, notificationB: Notification): boolean {
    let noUpdate = true;
    switch (notificationA.type) {
      case this.OPENING_NOTIFICATION:
        notificationA.text.forEach(notificationAText => {
          notificationB.text.forEach(notificationBText => {
            if (notificationAText.languageCode === notificationBText.languageCode) {
              if (noUpdate) {
                noUpdate = notificationAText.text === notificationBText.text;
              }
            }
          });
        });
        break;
      case this.CLOSING_NOTIFICATION:
        noUpdate = this.notificationWasUpdated(notificationA, notificationB);
        break;
      case this.REMINDER_NOTIFICATION:
        noUpdate = this.notificationWasUpdated(notificationA, notificationB);
        break;
    }
    return noUpdate;
  }

  notificationWasUpdated(notificationA: Notification, notificationB: Notification): boolean {
    let noUpdate = true;
    notificationA.text.forEach(notificationAText => {
      notificationB.text.forEach(notificationBText => {
        if (notificationAText.languageCode === notificationBText.languageCode) {
          if (noUpdate) {
            noUpdate = notificationAText.text === notificationBText.text;
          }
        }
      });
    });
    if (noUpdate) {
      if (notificationA.timing_type === this.INTERVAL_TIMING_TYPE) {
        return noUpdate = notificationA.timing_interval === notificationB.timing_interval;
      } else if (notificationA.timing_type === this.DATE_TIMING_TYPE) {
        return noUpdate = notificationA.date === notificationB.date;
      }
    }
    return noUpdate;
  }

  addDateToNotification(value, notification: Notification) {
    if (value) {
      notification.date = moment(value).format(API_DATE_FORMAT);
    } else {
      notification.date = null;
    }
  }

  changeTimingType(event, notification: Notification) {
    if (event === this.INTERVAL_TIMING_TYPE) {
      notification.date = null;
    } else if (event === this.DATE_TIMING_TYPE) {
      notification.timing_interval = 0;
      notification.weeks = null;
      notification.days = null;
      notification.hours = null;
      notification.minutes = null;
    }
  }

  notificationsHasErrors(): boolean {
    let hasError = false
    if (this.openNotificationCardView) {
      if (this.openNotificationCardView.errors.length > 0) {
        hasError = true;
      }
    }
    if (this.closeNotificationCardView) {
      if (this.closeNotificationCardView.errors.length > 0) {
        hasError = true;
      }
    }

    if (this.reminderNotificationCardViews) {
      this.reminderNotificationCardViews.forEach(reminder => {
        if (reminder.errors.length > 0) {
          hasError = true;
        }
        if (hasError) {
          return;
        }
      });
    }
    return hasError;
  }
}
